const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

/* DAYVEE MEGA-MENÜ – DYNAMISCHE PRODUKTROTATION */
(() => {
  const ROOT_SELECTOR = '[data-dayvee-mega-menu]';
  const stateByPanel = new WeakMap();

  const routeRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const collectionHandleFromPanel = (panel) => {
    const link = panel.querySelector('.dmm-context-title a[href*="/collections/"]');
    if (!link) return '';
    try {
      const url = new URL(link.href, window.location.origin);
      const match = url.pathname.match(/\/collections\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      return '';
    }
  };

  const normalizeTags = (tags) => {
    if (Array.isArray(tags)) return tags;
    if (!tags) return [];
    return String(tags).split(',');
  };

  const isTechnicalTestProduct = (product) => {
    const title = String(product?.title || '').toLowerCase();
    const handle = String(product?.handle || '').toLowerCase();
    const tags = normalizeTags(product?.tags).map((tag) => String(tag).trim().toLowerCase());

    return (
      title.includes('testprodukt') ||
      handle.includes('testprodukt') ||
      tags.some((tag) => ['test', 'testprodukt', 'technical-test', 'technical test'].includes(tag))
    );
  };

  const productKey = (product) => String(product?.id || product?.handle || '');
  const selectionSignature = (products) =>
    products.map(productKey).filter(Boolean).sort().join('|');

  const shuffle = (items) => {
    const copy = items.slice();
    for (let index = copy.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [copy[index], copy[randomIndex]] = [copy[randomIndex], copy[index]];
    }
    return copy;
  };

  const chooseProducts = (pool, previousSignature) => {
    const count = Math.min(3, pool.length);
    const shuffled = shuffle(pool);
    const selected = shuffled.slice(0, count);

    if (pool.length > count && selectionSignature(selected) === previousSignature) {
      selected[count - 1] = shuffled[count];
    }

    return selected;
  };

  const storageGet = (key) => {
    try {
      return window.sessionStorage.getItem(key) || '';
    } catch (error) {
      return '';
    }
  };

  const storageSet = (key, value) => {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {}
  };

  const productImage = (product) => {
    if (product?.image?.src) return product.image.src;
    if (typeof product?.featured_image === 'string') return product.featured_image;
    if (product?.featured_image?.src) return product.featured_image.src;

    const firstImage = Array.isArray(product?.images) ? product.images[0] : null;
    if (typeof firstImage === 'string') return firstImage;
    return firstImage?.src || '';
  };

  const imageWithWidth = (source, width) => {
    if (!source) return '';
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return source;
    }
  };

  const preloadImage = (source) =>
    new Promise((resolve) => {
      if (!source) {
        resolve();
        return;
      }

      const image = new Image();
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      image.onload = finish;
      image.onerror = finish;
      image.src = imageWithWidth(source, 420);

      if (image.complete) finish();
    });

  const preloadProductImages = (products) =>
    Promise.all(products.map((product) => preloadImage(productImage(product))));

  const productPrice = (product) => {
    const prices = (Array.isArray(product?.variants) ? product.variants : [])
      .map((variant) => Number(variant?.price))
      .filter((price) => Number.isFinite(price));

    if (prices.length) return Math.min(...prices);

    const fallback = Number(product?.price);
    return Number.isFinite(fallback) ? fallback : null;
  };

  const formatPrice = (product) => {
    const amount = productPrice(product);
    if (amount === null) return '';

    const currency = window.Shopify?.currency?.active || 'EUR';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (error) {
      return `${amount.toFixed(2).replace('.', ',')} €`;
    }
  };

  const productUrl = (product) =>
    `${routeRoot()}products/${encodeURIComponent(product.handle || '')}`;

  const createProductCard = (product) => {
    const title = String(product?.title || 'Produkt');
    const card = document.createElement('a');
    card.className = 'dmm-product';
    card.href = productUrl(product);
    card.setAttribute('aria-label', `${title} ansehen`);

    const media = document.createElement('div');
    media.className = 'dmm-product-media';

    const imageSource = productImage(product);

    if (imageSource) {
      const image = document.createElement('img');
      image.className = 'dmm-product-image';
      image.loading = 'eager';
      image.decoding = 'async';
      image.alt = title;
      image.src = imageWithWidth(imageSource, 420);
      media.appendChild(image);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'dmm-product-placeholder';
      media.appendChild(placeholder);
    }

    const copy = document.createElement('div');
    copy.className = 'dmm-product-copy';

    if (product?.vendor) {
      const vendor = document.createElement('span');
      vendor.className = 'dmm-product-vendor';
      vendor.textContent = String(product.vendor);
      copy.appendChild(vendor);
    }

    const titleNode = document.createElement('span');
    titleNode.className = 'dmm-product-title';
    titleNode.textContent = title;
    copy.appendChild(titleNode);

    const price = document.createElement('div');
    price.className = 'dmm-product-price';
    price.textContent = formatPrice(product);
    copy.appendChild(price);

    card.append(media, copy);
    return card;
  };

  const renderProducts = (grid, products) => {
    grid.replaceChildren(...products.map(createProductCard));
  };

  const loadPool = (state) => {
    if (state.pool) return Promise.resolve(state.pool);
    if (state.loadingPromise) return state.loadingPromise;

    const endpoint =
      `${routeRoot()}collections/${encodeURIComponent(state.handle)}/products.json?limit=50`;

    state.loadingPromise = fetch(endpoint, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        const products = Array.isArray(payload?.products) ? payload.products : [];
        state.pool = products.filter((product) => !isTechnicalTestProduct(product));
        state.loadingPromise = null;
        return state.pool;
      })
      .catch((error) => {
        state.loadingPromise = null;
        console.warn(
          '[Dayvee Mega-Menü] Dynamische Produktrotation nicht verfügbar; Editor-Fallback bleibt aktiv.',
          error
        );
        return null;
      });

    return state.loadingPromise;
  };

  const commitSelection = (panel, state, products) => {
    if (panel.classList.contains('is-active')) {
      state.pendingSelection = products;
      return false;
    }

    const grid = panel.querySelector('.dmm-products');
    if (!grid) return false;

    renderProducts(grid, products);

    const signature = selectionSignature(products);
    state.lastSignature = signature;
    state.pendingSelection = null;
    storageSet(`dayvee-mega-menu:last:${state.handle}`, signature);

    return true;
  };

  const prepareNextSelection = (panel, state) => {
    if (state.preparing) return;
    state.preparing = true;

    loadPool(state)
      .then((pool) => {
        if (!Array.isArray(pool) || pool.length === 0) return null;

        const storageKey = `dayvee-mega-menu:last:${state.handle}`;
        const previousSignature = state.lastSignature || storageGet(storageKey);
        const selected = chooseProducts(pool, previousSignature);

        return preloadProductImages(selected).then(() => selected);
      })
      .then((selected) => {
        state.preparing = false;
        if (!selected) return;

        if (panel.classList.contains('is-active')) {
          state.pendingSelection = selected;
          return;
        }

        commitSelection(panel, state, selected);
      })
      .catch((error) => {
        state.preparing = false;
        console.warn(
          '[Dayvee Mega-Menü] Vorbereitung der nächsten Produktauswahl fehlgeschlagen; aktuelle Auswahl bleibt sichtbar.',
          error
        );
      });
  };

  const initPanel = (panel) => {
    const handle = collectionHandleFromPanel(panel);
    if (!handle) return;

    const state = {
      handle,
      pool: null,
      loadingPromise: null,
      lastSignature: '',
      pendingSelection: null,
      preparing: false,
      wasActive: panel.classList.contains('is-active'),
    };

    stateByPanel.set(panel, state);

    const observer = new MutationObserver(() => {
      const active = panel.classList.contains('is-active');

      if (state.wasActive && !active) {
        if (state.pendingSelection) {
          commitSelection(panel, state, state.pendingSelection);
        } else {
          prepareNextSelection(panel, state);
        }
      }

      state.wasActive = active;
    });

    observer.observe(panel, {
      attributes: true,
      attributeFilter: ['class'],
    });

    /*
      Auswahl und Bilder werden vorbereitet, bevor der Nutzer das Menü öffnet.
      Ist das Panel bereits offen, wird die vorbereitete Auswahl nur vorgemerkt
      und erst nach dem Schließen unsichtbar eingesetzt.
    */
    prepareNextSelection(panel, state);
  };

  const initRoot = (root) => {
    if (!root || root.dataset.dayveeProductRotationReady === 'true') return;

    root.dataset.dayveeProductRotationReady = 'true';
    root.querySelectorAll('[data-dmm-panel]').forEach(initPanel);
  };

  const initAll = (root = document) => {
    if (root.matches?.(ROOT_SELECTOR)) initRoot(root);
    root.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target || document);
  });
})();

/* DAYVEE – LAUNCH POLISH: Startseiten-SEO-Fallback, Textglättungen */
(() => {
  const seoDescription = 'Ausgewählte Supplements, verständliche Wirkstoffinfos und alltagstaugliche Routinen – Dayvee hilft dir, Produkte besser einzuordnen.';

  const ensureHomeSeo = () => {
    if (window.location.pathname.replace(/\/+$/, '') !== '') return;

    if (!document.querySelector('meta[name="description"]')) {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = seoDescription;
      document.head.appendChild(meta);
    }

    if (document.title.trim().toLowerCase() === 'dayvee') {
      document.title = 'Dayvee – Supplements verstehen. Routinen finden.';
    }
  };

  const polishVisibleCopy = () => {
    const replacements = new Map([
      ['EPA und DHA & Herzfunktion', 'EPA und DHA: Herzfunktion'],
      ['Ein ausgewähltes Angebot für alle, die ihre Routine sinnvoll ergänzen möchten, da kommt man an Creatin nicht vorbei:', 'Ein ausgewähltes Angebot für alle, die Kreatin gezielt in ihre Trainingsroutine integrieren möchten.']
    ]);

    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let node;
    while ((node = walker.nextNode())) nodes.push(node);

    nodes.forEach((textNode) => {
      const exact = textNode.nodeValue.trim();
      if (!replacements.has(exact)) return;
      textNode.nodeValue = textNode.nodeValue.replace(exact, replacements.get(exact));
    });
  };

  ensureHomeSeo();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', polishVisibleCopy, { once: true });
  } else {
    polishVisibleCopy();
  }

  document.addEventListener('shopify:section:load', polishVisibleCopy);
})();

/* DAYVEE – QUIZ-ERGEBNIS: leere/nicht verfügbare Produktempfehlungen ausblenden */
(() => {
  const pickerHasAvailableProduct = (picker) => {
    const json = picker && picker.querySelector('[data-dqr-product-json]');
    if (!json) return false;
    try {
      const variants = JSON.parse(json.textContent || '[]');
      return Array.isArray(variants) && variants.some((variant) => variant && variant.available === true);
    } catch (error) {
      return false;
    }
  };

  const applyResultProductAvailability = (root) => {
    if (!root) return;

    const optionCards = Array.from(root.querySelectorAll('[data-option-card]'));
    optionCards.forEach((card) => {
      if (!pickerHasAvailableProduct(card)) card.hidden = true;
    });

    const optionsSection = root.querySelector('.dqr__options');
    if (optionsSection) {
      optionsSection.hidden = root.querySelectorAll('[data-option-card]:not([hidden])').length === 0;
    }

    const mainCard = root.querySelector('.dqr__routine-card');
    if (mainCard) {
      const hasMainProduct = pickerHasAvailableProduct(mainCard);
      mainCard.classList.toggle('dqr__routine-card--info-only', !hasMainProduct);
      const image = mainCard.querySelector('.dqr__main-image');
      const buy = mainCard.querySelector('.dqr__buy-panel');
      if (image) image.hidden = !hasMainProduct;
      if (buy) buy.hidden = !hasMainProduct;
    }

    const finalSection = root.querySelector('.dqr__final');
    if (finalSection) {
      const finalPicker = finalSection.querySelector('[data-dqr-product-picker]');
      finalSection.hidden = !pickerHasAvailableProduct(finalPicker);
    }
  };

  const applyAll = (scope = document) => {
    if (scope.matches?.('.dqr')) applyResultProductAvailability(scope);
    scope.querySelectorAll?.('.dqr').forEach(applyResultProductAvailability);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => applyAll(document), { once: true });
  } else {
    applyAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => applyAll(event.target || document));
})();
